// Study Hall message formatting (DESIGN.md section 7): a small, safe subset of Markdown.
//
//   **bold**  *italic* or _italic_  ~~strike~~  `code`  ```fenced code```  "> quote" at line start
//   backslash escapes, http(s) auto-links (the visible text is always the literal URL), @mentions
//
// Not supported, on purpose: headings, lists, images, HTML, spoilers and masked links, so
// "[x](javascript:...)", "<img onerror=...>" and "||spoiler||" all stay literal text.
//
// parse(text) returns a list of nodes {k, v?, c?} with k in text|b|i|s|code|pre|quote|link|mention|br
// (a list rather than one root node, since a message is a run of inline and block nodes).
// toDom(nodes) builds DOM with createElement/createTextNode/textContent only. Pure and small
// enough to run in Node, so test/chat.client.test.mjs checks the tree directly.

const WORD = /[A-Za-z0-9_]/;
const PUNCT = /[!-/:-@[-`{-~]/; // ASCII punctuation: what a backslash may escape
const URL_RE = /https?:\/\/[^\s<>"'`]+/iy;
const MENTION_RE = /[A-Za-z0-9_.-]{2,24}/y;
const FENCE_OPEN = /^```[A-Za-z0-9_+-]{0,20}\s*$/;
const FENCE_CLOSE = /^```\s*$/;
const MAX_DEPTH = 6; // nested emphasis deeper than this stays literal

// True when s has whitespace or a control character (a loop, not a regex: no-control-regex).
export function hasCtl(s) {
	for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); if (c <= 32 || c === 127) return true; }
	return false;
}

// A link target we are willing to render: http(s), a host, no whitespace or control characters.
export function safeUrl(raw) {
	if (typeof raw !== "string" || !raw || raw.length > 2048 || hasCtl(raw)) return null;
	let u;
	try { u = new URL(raw); } catch (e) { void e; return null; }
	if ((u.protocol !== "http:" && u.protocol !== "https:") || !u.hostname) return null;
	return u;
}

// Drop trailing sentence punctuation from an auto-link, the way the server's link policy does,
// except that a ")" closing a "(" inside the URL (Wikipedia style) is kept.
export function trimUrl(raw) {
	let s = raw;
	for (;;) {
		const c = s.charAt(s.length - 1);
		if (c && ".,!?]:;".includes(c)) s = s.slice(0, -1);
		else if (c === ")" && (s.split("(").length - 1) < (s.split(")").length - 1)) s = s.slice(0, -1);
		else return s;
	}
}

// The URL starting at i (at a word boundary), or null.
function urlAt(s, i) {
	const c = s[i];
	if ((c !== "h" && c !== "H") || WORD.test(s[i - 1] || "")) return null;
	URL_RE.lastIndex = i;
	const m = URL_RE.exec(s);
	if (!m) return null;
	const raw = trimUrl(m[0]);
	return safeUrl(raw) ? raw : null;
}

// The emphasis delimiter opening at i, or null. Openers must be followed by a non-space; "_" must
// also start a word, and runs of "__" stay literal (snake_case and __dunder__ never italicize).
function opener(s, i) {
	const next = s[i + 1];
	if (s.startsWith("**", i)) return s[i + 2] && !/\s/.test(s[i + 2]) ? { m: "**", k: "b" } : null;
	if (s.startsWith("~~", i)) return s[i + 2] && !/\s/.test(s[i + 2]) ? { m: "~~", k: "s" } : null;
	if (s[i] === "*") return next && !/\s/.test(next) ? { m: "*", k: "i" } : null;
	if (s[i] === "_" && next && !/\s/.test(next) && next !== "_" && !WORD.test(s[i - 1] || "")) return { m: "_", k: "i" };
	return null;
}

// Where the delimiter opened before `from` closes, or -1. Skips escapes, code spans and URLs so a
// "*" or "_" inside them never closes anything.
function closer(s, from, d) {
	for (let j = from; j < s.length; j++) {
		const c = s[j];
		if (c === "\\") { j++; continue; }
		if (c === "`") {
			const f = s.startsWith("```", j) ? "```" : "`";
			const e = s.indexOf(f, j + f.length);
			if (e !== -1) { j = e + f.length - 1; continue; }
		}
		const url = urlAt(s, j);
		if (url) { j += url.length - 1; continue; }
		if (j === from || !s.startsWith(d.m, j) || /\s/.test(s[j - 1])) continue;
		if (d.m === "*" && (s[j + 1] === "*" || s[j - 1] === "*")) continue;
		if (d.m === "_" && (s[j + 1] === "_" || s[j - 1] === "_" || WORD.test(s[j + 1] || ""))) continue;
		return j;
	}
	return -1;
}

function inline(s, depth) {
	const out = [];
	let buf = "";
	const flush = () => { if (buf) { out.push({ k: "text", v: buf }); buf = ""; } };
	let i = 0;
	while (i < s.length) {
		const c = s[i];
		if (c === "\\" && i + 1 < s.length && PUNCT.test(s[i + 1])) { buf += s[i + 1]; i += 2; continue; }
		if (c === "\n") { flush(); out.push({ k: "br" }); i++; continue; }
		if (c === "`") {
			const f = s.startsWith("```", i) ? "```" : "`";
			const e = s.indexOf(f, i + f.length);
			if (e !== -1 && s.slice(i + f.length, e).trim()) {
				flush();
				out.push({ k: "code", v: s.slice(i + f.length, e) });
				i = e + f.length;
				continue;
			}
			buf += f; i += f.length;
			continue;
		}
		const url = urlAt(s, i);
		if (url) { flush(); out.push({ k: "link", v: url }); i += url.length; continue; }
		if (c === "@" && !WORD.test(s[i - 1] || "")) {
			MENTION_RE.lastIndex = i + 1;
			const m = MENTION_RE.exec(s);
			const t = m ? m[0].replace(/[.-]+$/, "") : "";
			if (t.length >= 2) { flush(); out.push({ k: "mention", v: "@" + t }); i += 1 + t.length; continue; }
		}
		if (depth < MAX_DEPTH) {
			const d = opener(s, i);
			if (d) {
				const e = closer(s, i + d.m.length, d);
				if (e !== -1) {
					flush();
					out.push({ k: d.k, c: inline(s.slice(i + d.m.length, e), depth + 1) });
					i = e + d.m.length;
					continue;
				}
			}
		}
		buf += c; i++;
	}
	flush();
	return out;
}

export function parse(text) {
	const lines = String(text == null ? "" : text).replace(/\r\n?/g, "\n").split("\n");
	const out = [];
	let para = [];
	const flush = () => {
		if (!para.length) return;
		out.push(...inline(para.join("\n"), 0));
		para = [];
	};
	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		if (FENCE_OPEN.test(line)) {
			let j = i + 1;
			while (j < lines.length && !FENCE_CLOSE.test(lines[j])) j++;
			if (j < lines.length) {
				flush();
				out.push({ k: "pre", v: lines.slice(i + 1, j).join("\n") });
				i = j;
				continue;
			}
		}
		if (line === ">" || line.startsWith("> ")) {
			const q = [];
			while (i < lines.length && (lines[i] === ">" || lines[i].startsWith("> "))) q.push(lines[i++].slice(2));
			i--;
			flush();
			out.push({ k: "quote", c: inline(q.join("\n"), 0) });
			continue;
		}
		para.push(line);
	}
	flush();
	return out;
}

// The words of a message without its formatting marks, on one line (reply previews).
export function plain(text) {
	const flat = (nodes) => nodes.map((n) => (n.k === "br" ? " " : n.k === "quote" || n.k === "pre" ? " " + (n.c ? flat(n.c) : n.v) + " " : n.c ? flat(n.c) : n.v || "")).join("");
	return flat(parse(text)).replace(/\s+/g, " ").trim();
}

const TAGS = { b: "strong", i: "em", s: "s", quote: "blockquote" };

function build(n, doc) {
	switch (n.k) {
		case "text": return doc.createTextNode(n.v);
		case "br": return doc.createElement("br");
		case "code": { const e = doc.createElement("code"); e.textContent = n.v; return e; }
		case "pre": { const p = doc.createElement("pre"); const c = doc.createElement("code"); c.textContent = n.v; p.appendChild(c); return p; }
		case "mention": { const e = doc.createElement("span"); e.className = "rv-hall-mention"; e.textContent = n.v; return e; }
		case "link": {
			const u = safeUrl(n.v);
			if (!u) return doc.createTextNode(n.v);
			const a = doc.createElement("a");
			a.href = u.href;
			a.textContent = n.v; // always the literal URL, never a label
			a.rel = "noopener noreferrer nofollow";
			a.referrerPolicy = "no-referrer";
			a.setAttribute("data-ext", "");
			return a;
		}
		default: {
			const tag = TAGS[n.k];
			if (!tag) return doc.createTextNode("");
			const e = doc.createElement(tag);
			e.appendChild(toDom(n.c || [], doc));
			return e;
		}
	}
}

export function toDom(nodes, doc = document) {
	const frag = doc.createDocumentFragment();
	for (const n of nodes) frag.appendChild(build(n, doc));
	return frag;
}
